import React from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import type { LeaveRequestDisplayItem } from "../types";
import { useResponsive } from "@/hooks/use-responsive";

interface LeaveRequestDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: LeaveRequestDisplayItem | null;
}

function formatDate(value: string): string {
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

export function LeaveRequestDetailsModal({
  open,
  onOpenChange,
  request,
}: LeaveRequestDetailsModalProps) {
  const { isDesktop } = useResponsive();
  if (!request) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={isDesktop ? "w-1/2 sm:max-w-none overflow-y-auto" : "w-full sm:max-w-none overflow-y-auto"}
        data-testid="leave-request-details-modal"
      >
        <SheetHeader>
          <SheetTitle>Leave request details</SheetTitle>
          <SheetDescription>
            Review full details for this leave request.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-3 text-sm mt-6">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Status</span>
            <span className="font-medium capitalize">{request.status}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Start date</span>
            <span className="font-medium">{formatDate(request.start_date)}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">End date</span>
            <span className="font-medium">{formatDate(request.end_date)}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Requested days</span>
            <span className="font-medium">{request.requested_days}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Booking unit</span>
            <span className="font-medium capitalize">{request.booking_unit}</span>
          </div>
          {request.reason ? (
            <div>
              <p className="text-muted-foreground">Comment</p>
              <p className="font-medium break-words">{request.reason}</p>
            </div>
          ) : null}
          {request.rejection_reason ? (
            <div>
              <p className="text-muted-foreground">Rejection reason</p>
              <p className="font-medium break-words">{request.rejection_reason}</p>
            </div>
          ) : null}
        </div>

        <SheetFooter className="mt-8">
          <Button type="button" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
